// Estado, píldoras, filtros y orden de la lista de Clientes. Lógica pura, sin React.
import { ClientLevelEnum } from '@fiado/type-kit/bin/loanScoring/enums/ClientLevelEnum';
import type {
    CustomersTableCustomerType,
    CustomersTableItem,
} from '@/api/services/retail-wizard/backoffice';
import type { RetailUser, Store } from '@/api/services/retail-org/retail-org.client';
import { daysSinceMx, mxDayOffset } from '@/lib/dateHelpers';
import { ACCOUNT_ONLY_LABEL } from '@/shared/lib/format/accountOnly';
import { isSeller } from '../../lib/roster';

/** Estado por recencia (`estadoClienteEnTenant` del mock); «solo cuenta» si nunca ha comprado. */
export type CustomerStatus = 'ACTIVE' | 'NEW' | 'REGULAR' | 'DORMANT' | 'ACCOUNT_ONLY';

/** Nivel SK y crédito vivo de un cliente, como los cruza la tabla con SureKeep. */
export interface CustomerCredit {
    level: ClientLevelEnum | null;
    hasActiveCredit: boolean;
}

/** Si SureKeep contestó. Fuera de `ready` el nivel se pinta «—». */
export type CreditReadState = 'loading' | 'ready' | 'forbidden' | 'error';

/** Lo que la tabla necesita de un cliente, venga del endpoint de tabla o del respaldo. */
export interface CustomerBaseRow {
    retailCustomerId: string;
    name: string | null;
    phone: string | null;
    /** Abrió su cuenta PCF y no tiene ninguna venta. */
    accountOnly: boolean;
    /** Tienda donde se dio de alta; el nombre sale del padrón de tiendas. */
    originStoreId: string | null;
    originStoreName: string | null;
    purchaseCount: number | null;
    totalSpentCents: number | null;
    averageTicketCents: number | null;
    lastPurchaseAt: string | null;
    favoriteStoreName: string | null;
    /** `undefined` cuando SureKeep no dio dato: no es «sin nivel». */
    credit: CustomerCredit | undefined;
}

export interface CustomerListRow extends CustomerBaseRow {
    daysSincePurchase: number | null;
    status: CustomerStatus | null;
}

/**
 * Más de 90 días = dormido; 31 a 90 = regular; 30 o menos = activo, o nuevo si fue su única compra.
 * Sin número de compras no se distingue nuevo de activo y queda activo.
 */
export function statusOf(days: number | null, purchaseCount: number | null): CustomerStatus | null {
    if (days === null) return null;
    if (days > 90) return 'DORMANT';
    if (days > 30) return 'REGULAR';
    return purchaseCount === 1 ? 'NEW' : 'ACTIVE';
}

export function withDerived(row: CustomerBaseRow): CustomerListRow {
    if (row.accountOnly) return { ...row, daysSincePurchase: null, status: 'ACCOUNT_ONLY' };
    const days = row.lastPurchaseAt === null ? null : daysSinceMx(row.lastPurchaseAt);
    return { ...row, daysSincePurchase: days, status: statusOf(days, row.purchaseCount) };
}

/** Nivel y crédito vivo de la fila; sin ninguno de los dos, SureKeep no dio dato. */
export function creditOfItem(item: CustomersTableItem): CustomerCredit | undefined {
    if (item.skLevel === null && item.activeCreditsCount === null) return undefined;
    return {
        level: item.skLevel === null ? null : ClientLevelEnum[item.skLevel],
        hasActiveCredit: (item.activeCreditsCount ?? 0) > 0,
    };
}

export function toListRows(items: CustomersTableItem[]): CustomerListRow[] {
    return items.map((item) =>
        withDerived({
            retailCustomerId: item.retailCustomerId,
            name: item.name,
            phone: item.phone,
            accountOnly: item.accountOnly,
            originStoreId: item.originStoreId,
            originStoreName: null,
            purchaseCount: item.purchaseCount,
            totalSpentCents: item.totalSpentCents,
            averageTicketCents: item.averageTicketCents,
            lastPurchaseAt: item.lastPurchaseAt,
            favoriteStoreName: item.favoriteStoreName,
            credit: creditOfItem(item),
        }),
    );
}

export type Bucket =
    | 'ALL'
    | 'ACTIVE'
    | 'DORMANT'
    | 'REBUY'
    | 'RECURRING'
    | 'WITH_CREDIT'
    | 'ACCOUNT_ONLY';

/** Conteo por píldora sobre lo cargado. `null` = la píldora no tiene fuente. */
export type BucketCounts = Record<Bucket, number | null>;

/** `accountOnlyCount` es la suma del servidor; sin ella se cuentan las filas cargadas. */
export function bucketCounts(
    rows: CustomerListRow[],
    hasAggregates: boolean,
    creditState: CreditReadState,
    accountOnlyCount: number | null = null,
): BucketCounts {
    const count = (match: (row: CustomerListRow) => boolean) => rows.filter(match).length;
    return {
        ALL: rows.length,
        ACTIVE: count((r) => r.status === 'ACTIVE' || r.status === 'NEW'),
        DORMANT: count((r) => r.status === 'DORMANT'),
        REBUY: null,
        RECURRING: hasAggregates ? count((r) => (r.purchaseCount ?? 0) >= 2) : null,
        WITH_CREDIT:
            creditState === 'ready' ? count((r) => r.credit?.hasActiveCredit === true) : null,
        ACCOUNT_ONLY: accountOnlyCount ?? count((r) => r.accountOnly),
    };
}

function inBucket(row: CustomerListRow, bucket: Bucket): boolean {
    switch (bucket) {
        case 'ALL':
            return true;
        case 'ACTIVE':
            return row.status === 'ACTIVE' || row.status === 'NEW';
        case 'DORMANT':
            return row.status === 'DORMANT';
        case 'RECURRING':
            return (row.purchaseCount ?? 0) >= 2;
        case 'WITH_CREDIT':
            return row.credit?.hasActiveCredit === true;
        case 'ACCOUNT_ONLY':
            return row.accountOnly;
        case 'REBUY':
            return false;
    }
}

/** Los chips de nivel del filtro; `NONE` junta «sin nivel» y «no elegible». */
export type LevelFilter = 'GOLD' | 'SILVER' | 'BRONZE' | 'NONE';

export const LEVEL_FILTERS: readonly LevelFilter[] = ['GOLD', 'SILVER', 'BRONZE', 'NONE'];

function levelFilterOf(level: ClientLevelEnum | null): LevelFilter {
    if (level === ClientLevelEnum.GOLD) return 'GOLD';
    if (level === ClientLevelEnum.SILVER) return 'SILVER';
    if (level === ClientLevelEnum.BRONZE) return 'BRONZE';
    return 'NONE';
}

/** Atajos de «Última compra». `CUSTOM` usa las fechas tecleadas. */
export type DatePreset = 'LAST_30' | 'LAST_90' | 'LAST_180' | 'LAST_365' | 'ALL' | 'CUSTOM';

export const DEFAULT_DATE_PRESET: DatePreset = 'LAST_90';

export const DATE_PRESET_LABEL: Record<DatePreset, string> = {
    LAST_30: 'Últimos 30 días',
    LAST_90: 'Últimos 90 días',
    LAST_180: 'Últimos 180 días',
    LAST_365: 'Último año',
    ALL: 'Todo',
    CUSTOM: 'Personalizado',
};

export const DATE_PRESETS: readonly DatePreset[] = [
    'LAST_30',
    'LAST_90',
    'LAST_180',
    'LAST_365',
    'ALL',
    'CUSTOM',
];

const PRESET_DAYS: Partial<Record<DatePreset, number>> = {
    LAST_30: 30,
    LAST_90: 90,
    LAST_180: 180,
    LAST_365: 365,
};

/** «Tipo de cliente» del panel; lo resuelve el servidor. */
export type CustomerTypeFilter = 'ALL' | 'WITH_SALES' | 'ACCOUNT_ONLY';

export const CUSTOMER_TYPE_FILTERS: readonly CustomerTypeFilter[] = [
    'ALL',
    'WITH_SALES',
    'ACCOUNT_ONLY',
];

export const CUSTOMER_TYPE_LABEL: Record<CustomerTypeFilter, string> = {
    ALL: 'Todos',
    WITH_SALES: 'Con compras',
    ACCOUNT_ONLY: ACCOUNT_ONLY_LABEL,
};

const CUSTOMER_TYPE_PARAM: Record<CustomerTypeFilter, CustomersTableCustomerType | null> = {
    ALL: null,
    WITH_SALES: 'withSales',
    ACCOUNT_ONLY: 'accountOnly',
};

export interface CustomersFilters {
    query: string;
    customerType: CustomerTypeFilter;
    storeId: string;
    sellerId: string;
    levels: LevelFilter[];
    /** Rango de compras, como lo teclea el operador. */
    purchasesMin: string;
    purchasesMax: string;
    datePreset: DatePreset;
    /** Última compra con `CUSTOM`, días de calendario `YYYY-MM-DD`. */
    from: string;
    to: string;
    bucket: Bucket;
}

export const EMPTY_FILTERS: CustomersFilters = {
    query: '',
    customerType: 'ALL',
    storeId: '',
    sellerId: '',
    levels: [],
    purchasesMin: '',
    purchasesMax: '',
    datePreset: DEFAULT_DATE_PRESET,
    from: '',
    to: '',
    bucket: 'ALL',
};

/** Los filtros que resuelve el servidor; cambiarlos reinicia la paginación. */
export interface CustomersServerFilters {
    from: string | null;
    to: string | null;
    storeId: string | null;
    sellerId: string | null;
    /** `null` = todos; no viaja. */
    customerType: CustomersTableCustomerType | null;
}

export function serverFiltersOf(filters: CustomersFilters): CustomersServerFilters {
    const days = PRESET_DAYS[filters.datePreset];
    const custom = filters.datePreset === 'CUSTOM';
    return {
        from: days !== undefined ? mxDayOffset(days) : custom && filters.from ? filters.from : null,
        to: custom && filters.to ? filters.to : null,
        storeId: filters.storeId || null,
        sellerId: filters.sellerId || null,
        customerType: CUSTOMER_TYPE_PARAM[filters.customerType],
    };
}

function toInt(raw: string): number | null {
    if (raw.trim() === '') return null;
    const value = Number(raw);
    return Number.isFinite(value) ? value : null;
}

/** Filtros sobre lo cargado, en AND; los chips de nivel son OR entre sí. */
export function filterCustomers(
    rows: CustomerListRow[],
    filters: CustomersFilters,
): CustomerListRow[] {
    const query = filters.query.trim().toLowerCase();
    const min = toInt(filters.purchasesMin);
    const max = toInt(filters.purchasesMax);
    return rows.filter((row) => {
        if (query) {
            const haystack =
                `${row.name ?? ''} ${row.phone ?? ''} ${row.retailCustomerId}`.toLowerCase();
            if (!haystack.includes(query)) return false;
        }
        if (filters.levels.length > 0) {
            if (row.credit === undefined) return false;
            if (!filters.levels.includes(levelFilterOf(row.credit.level))) return false;
        }
        // Un rango sobre un conteo que no llegó no se puede evaluar: la fila queda fuera.
        if (min !== null && (row.purchaseCount ?? -1) < min) return false;
        if (max !== null && (row.purchaseCount === null || row.purchaseCount > max)) return false;
        return inBucket(row, filters.bucket);
    });
}

/** La tienda de la columna: la favorita o, en un cliente solo cuenta, la de alta. */
export function storeShownOf(row: CustomerBaseRow): string | null {
    return row.accountOnly ? row.originStoreName : row.favoriteStoreName;
}

/** Pone el nombre de la tienda de alta, que la fila trae solo como id. */
export function withOriginStoreNames(
    rows: CustomerListRow[],
    storeNames: ReadonlyMap<string, string>,
): CustomerListRow[] {
    if (!rows.some((r) => r.originStoreName === null && r.originStoreId !== null)) return rows;
    return rows.map((row) =>
        row.originStoreName === null && row.originStoreId !== null
            ? { ...row, originStoreName: storeNames.get(row.originStoreId) ?? null }
            : row,
    );
}

export type SortKey =
    | 'id'
    | 'name'
    | 'status'
    | 'level'
    | 'purchases'
    | 'total'
    | 'ticket'
    | 'days'
    | 'store';

export interface SortState {
    key: SortKey;
    direction: 'asc' | 'desc';
}

export const DEFAULT_SORT: SortState = { key: 'total', direction: 'desc' };

const ASCENDING_FIRST: readonly SortKey[] = ['id', 'name', 'level', 'store'];

const STATUS_ORDER: CustomerStatus[] = ['ACTIVE', 'NEW', 'REGULAR', 'DORMANT', 'ACCOUNT_ONLY'];

const LEVEL_ORDER: Record<LevelFilter, number> = { GOLD: 0, SILVER: 1, BRONZE: 2, NONE: 3 };

function sortValue(row: CustomerListRow, key: SortKey): string | number | null {
    switch (key) {
        case 'id':
            return row.retailCustomerId;
        case 'name':
            return row.name;
        case 'status':
            return row.status === null ? null : STATUS_ORDER.indexOf(row.status);
        case 'level':
            return row.credit === undefined ? null : LEVEL_ORDER[levelFilterOf(row.credit.level)];
        case 'purchases':
            return row.purchaseCount;
        case 'total':
            return row.totalSpentCents;
        case 'ticket':
            return row.averageTicketCents;
        case 'days':
            return row.daysSincePurchase;
        case 'store':
            return storeShownOf(row);
    }
}

/** Los huecos van al final en los dos sentidos; el empate se rompe por nombre, A→Z. */
export function sortCustomers(rows: CustomerListRow[], sort: SortState): CustomerListRow[] {
    const sign = sort.direction === 'asc' ? 1 : -1;
    const byName = (a: CustomerListRow, b: CustomerListRow) =>
        (a.name ?? '').localeCompare(b.name ?? '', 'es-MX');
    return [...rows].sort((a, b) => {
        const va = sortValue(a, sort.key);
        const vb = sortValue(b, sort.key);
        if (va === null || vb === null) {
            if (va === vb) return byName(a, b);
            return va === null ? 1 : -1;
        }
        const diff =
            typeof va === 'string' && typeof vb === 'string'
                ? va.localeCompare(vb, 'es-MX')
                : Number(va) - Number(vb);
        return diff === 0 ? byName(a, b) : sign * diff;
    });
}

/** Repetir la columna invierte el sentido; una nueva arranca A→Z en texto y de mayor a menor en cifras. */
export function nextSort(current: SortState, key: SortKey): SortState {
    if (current.key === key) {
        return { key, direction: current.direction === 'asc' ? 'desc' : 'asc' };
    }
    return { key, direction: ASCENDING_FIRST.includes(key) ? 'asc' : 'desc' };
}

export interface Option {
    id: string;
    name: string;
}

/** Tiendas del padrón, A→Z. */
export function storeOptions(stores: readonly Store[]): Option[] {
    return sortOptions(new Map(stores.map((store) => [store.storeId, store.name])));
}

/** Vendedores del padrón; con tienda elegida, solo los de esa tienda. */
export function sellerOptions(users: readonly RetailUser[], storeId: string): Option[] {
    const byId = new Map<string, string>();
    for (const user of users) {
        if (!isSeller(user)) continue;
        const stores = [user.homeStoreId, ...(user.coverageStoreIds ?? [])];
        if (storeId && !stores.includes(storeId)) continue;
        byId.set(user.retailUserId, user.displayName ?? user.retailUserId);
    }
    return sortOptions(byId);
}

function sortOptions(byId: Map<string, string>): Option[] {
    return [...byId.entries()]
        .map(([id, name]) => ({ id, name }))
        .sort((a, b) => a.name.localeCompare(b.name, 'es-MX'));
}

/** Cuántos filtros del panel hay puestos (la píldora no cuenta: vive en la barra). */
export function countActiveFilters(filters: CustomersFilters): number {
    const fields = [
        filters.query,
        filters.storeId,
        filters.sellerId,
        filters.purchasesMin,
        filters.purchasesMax,
    ].filter((value) => value.trim() !== '').length;
    const dates = filters.datePreset === DEFAULT_DATE_PRESET ? 0 : 1;
    const type = filters.customerType === 'ALL' ? 0 : 1;
    return fields + dates + type + filters.levels.length;
}

export function toggleLevel(levels: LevelFilter[], level: LevelFilter): LevelFilter[] {
    return levels.includes(level) ? levels.filter((l) => l !== level) : [...levels, level];
}
